import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { router } from '@inertiajs/react'
import { 
  Send, 
  Search, 
  UsersRound, 
  Calendar, 
  BedDouble, 
  Timer, 
  BellRing,
  User,
  Sparkles,
  ArrowLeft,
  Filter,
  X,
  Clock,
  CheckCircle,
  MessageSquare
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useState, useMemo } from 'react'

interface Student {
  id: number
  name: string
  email: string
  phone: string
  avatar_url: string | null
  has_push_token: boolean
  push_enabled: boolean
  push_active: boolean
  push_registered_at: string | null
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

interface Template {
  id: number
  name: string
  title: string
  body: string
  icon: string
  category: string
}

interface AudienceInfo {
  total: number
  with_push: number
  without_push: number
  can_send: boolean
}

interface AudiencesStats {
  all_students: AudienceInfo
  students_with_lesson_today: AudienceInfo
  students_inactive: AudienceInfo
  students_package_expiring: AudienceInfo
  students_with_push: AudienceInfo
}

interface Props {
  students: Student[]
  templates: Template[]
  audiences_stats: AudiencesStats
}

export default function PushNotificationsNew({ students, templates, audiences_stats }: Props) {
  const [step, setStep] = useState(1) // 1 = Público, 2 = Mensagem
  const [selectedAudience, setSelectedAudience] = useState<string>('')
  const [selectedStudents, setSelectedStudents] = useState<number[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [pushFilter, setPushFilter] = useState<string>('all')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [audienceStudents, setAudienceStudents] = useState<Student[]>([])
  const [isLoadingAudience, setIsLoadingAudience] = useState(false)

  const audiences = [
    { 
      value: 'all_students', 
      label: 'Todos os Alunos', 
      icon: UsersRound,
      stats: audiences_stats.all_students
    },
    { 
      value: 'students_with_lesson_today', 
      label: 'Aula Hoje', 
      icon: Calendar,
      stats: audiences_stats.students_with_lesson_today
    },
    { 
      value: 'students_inactive', 
      label: 'Inativos (7+ dias)', 
      icon: BedDouble,
      stats: audiences_stats.students_inactive
    },
    { 
      value: 'students_package_expiring', 
      label: 'Pacote Vencendo', 
      icon: Timer,
      stats: audiences_stats.students_package_expiring
    },
    { 
      value: 'students_with_push', 
      label: 'Com Push Ativo', 
      icon: BellRing,
      stats: audiences_stats.students_with_push
    },
  ]

  // Filtrar estudantes (usa audienceStudents se houver audiência selecionada, senão usa todos)
  const filteredStudents = useMemo(() => {
    const studentsToFilter = selectedAudience !== '' ? audienceStudents : students
    
    return studentsToFilter.filter(student => {
      // Busca por nome ou email
      const matchesSearch = student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           student.email.toLowerCase().includes(searchQuery.toLowerCase())
      
      // Filtro de push
      let matchesPushFilter = true
      if (pushFilter === 'with_push') {
        matchesPushFilter = student.push_active
      } else if (pushFilter === 'without_push') {
        matchesPushFilter = !student.push_active
      }

      return matchesSearch && matchesPushFilter
    })
  }, [students, audienceStudents, selectedAudience, searchQuery, pushFilter])

  const handleAudienceSelect = async (audienceValue: string) => {
    setSelectedAudience(audienceValue)
    setSelectedStudents([])
    setAudienceStudents([])
    
    // Se selecionou uma audiência específica (não "Seleção Individual")
    if (audienceValue !== '') {
      setIsLoadingAudience(true)
      try {
        const response = await fetch(`/push_notifications/audience_preview?audience_type=${audienceValue}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          }
        })
        
        const data = await response.json()
        
        if (data.success) {
          const fetchedStudents = data.data.students as Student[]
          setAudienceStudents(fetchedStudents)
          // Pré-seleciona todos os alunos com push ativo
          const activeStudentIds = fetchedStudents
            .filter(s => s.push_active)
            .map(s => s.id)
          setSelectedStudents(activeStudentIds)
        }
      } catch (error) {
        console.error('Erro ao buscar alunos da audiência:', error)
      } finally {
        setIsLoadingAudience(false)
      }
    }
  }

  const handleStudentToggle = (studentId: number) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    )
  }

  const handleSelectAll = () => {
    const activeStudents = filteredStudents.filter(s => s.push_active).map(s => s.id)
    setSelectedStudents(activeStudents)
  }

  const handleClearSelection = () => {
    setSelectedStudents([])
  }

  const handleTemplateSelect = (template: Template) => {
    setTitle(template.title)
    setBody(template.body)
    // Não fecha o modal para permitir edição
  }

  const handleSubmit = () => {
    if (!title.trim() || !body.trim()) return
    
    const recipientCount = selectedStudents.filter(id => {
      const student = (selectedAudience !== '' ? audienceStudents : students).find(s => s.id === id)
      return student?.push_active
    }).length

    if (recipientCount === 0) return

    setIsSending(true)
    
    // Se selecionou uma audiência mas personalizou os alunos, envia como 'specific_students'
    const shouldUseSpecificStudents = selectedAudience !== '' && selectedStudents.length > 0
    
    router.post('/push_notifications', {
      audience_type: shouldUseSpecificStudents ? 'specific_students' : (selectedAudience || 'specific_students'),
      filters: { student_ids: selectedStudents },
      title,
      body
    }, {
      onSuccess: () => {
        router.visit('/push_notifications')
      },
      onError: () => {
        setIsSending(false)
      }
    })
  }

  const recipientCount = selectedStudents.filter(id => {
    const student = (selectedAudience !== '' ? audienceStudents : students).find(s => s.id === id)
    return student?.push_active
  }).length

  const canContinueToMessage = recipientCount > 0

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <Button
            variant="ghost"
            onClick={() => router.visit('/push_notifications')}
            className="mb-2 -ml-2"
            size="sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          <h1 className="text-2xl font-bold tracking-tight mb-1">Nova Notificação Push</h1>
          <p className="text-muted-foreground text-sm">
            Envie mensagens personalizadas para seus alunos
          </p>
        </div>

        {/* Steps Indicator - Compacto */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                step === 1 ? 'bg-primary text-primary-foreground' : 'bg-green-500 text-white'
              }`}>
                {step > 1 ? <CheckCircle className="h-4 w-4" /> : '1'}
              </div>
              <p className={`text-sm font-medium ${step === 1 ? 'text-foreground' : 'text-muted-foreground'}`}>
                Público Alvo
              </p>
            </div>
            
            <div className="flex-1 h-[2px] bg-gray-200 dark:bg-gray-800 max-w-[100px]" />
            
            <div className="flex items-center gap-2">
              <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                step === 2 ? 'bg-primary text-primary-foreground' : 'bg-gray-200 dark:bg-gray-800 text-muted-foreground'
              }`}>
                2
              </div>
              <p className={`text-sm font-medium ${step === 2 ? 'text-foreground' : 'text-muted-foreground'}`}>
                Conteúdo da Mensagem
              </p>
            </div>
          </div>
        </div>

        {/* Step 1: Público */}
        {step === 1 && (
          <div className="grid grid-cols-12 gap-6">
            {/* Sidebar - Audiências Rápidas */}
            <div className="col-span-3 space-y-2">
              <div className="mb-3">
                <h3 className="font-semibold text-sm">Selecionar Público</h3>
              </div>
            
            {audiences.map((audience) => {
              const IconComponent = audience.icon
              const isSelected = selectedAudience === audience.value
              
              return (
                <button
                  key={audience.value}
                  className={`w-full text-left p-2.5 rounded-lg border-2 transition-all hover:border-primary/50 ${
                    isSelected 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950'
                  }`}
                  onClick={() => handleAudienceSelect(audience.value)}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className={`p-1 rounded ${isSelected ? 'bg-primary/10' : 'bg-gray-100 dark:bg-gray-900'}`}>
                      <IconComponent className={`h-3.5 w-3.5 ${isSelected ? 'text-primary' : 'text-gray-600 dark:text-gray-400'}`} />
                    </div>
                    <span className="font-medium text-xs">{audience.label}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] ml-7">
                    <span className="text-muted-foreground">{audience.stats.total} total</span>
                    <span className="text-green-600 dark:text-green-500">
                      {audience.stats.with_push} push
                    </span>
                  </div>
                </button>
              )
            })}

            <div className="pt-2">
              <button
                className={`w-full text-left p-2.5 rounded-lg border-2 transition-all hover:border-primary/50 ${
                  selectedAudience === '' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950'
                }`}
                onClick={() => handleAudienceSelect('')}
              >
                <div className="flex items-center gap-2">
                  <div className={`p-1 rounded ${selectedAudience === '' ? 'bg-primary/10' : 'bg-gray-100 dark:bg-gray-900'}`}>
                    <User className={`h-3.5 w-3.5 ${selectedAudience === '' ? 'text-primary' : 'text-gray-600 dark:text-gray-400'}`} />
                  </div>
                  <span className="font-medium text-xs">Seleção Individual</span>
                </div>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-9 space-y-4">
            {/* Tabela de Alunos (para audiência selecionada OU seleção individual) */}
            {(selectedAudience !== '' || selectedAudience === '') && (
              <Card className="shadow-sm">
                <CardHeader className="border-b bg-gray-50 dark:bg-gray-900/50 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">
                        {selectedAudience !== '' 
                          ? audiences.find(a => a.value === selectedAudience)?.label
                          : 'Selecionar Alunos'
                        }
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {selectedAudience !== ''
                          ? 'Alunos deste grupo - você pode marcar/desmarcar para personalizar'
                          : 'Escolha individualmente quem vai receber a notificação'
                        }
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Público Estimado</p>
                        <p className="text-lg font-bold">{recipientCount} <span className="text-xs font-normal text-muted-foreground">dispositivos</span></p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setStep(2)}
                        disabled={!canContinueToMessage}
                        className="px-4"
                      >
                        Continuar para Mensagem
                        <MessageSquare className="ml-2 h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Filtros e Busca */}
                  <div className="p-3 bg-white dark:bg-gray-950 border-b">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Buscar por nome ou e-mail..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      <Select value={pushFilter} onValueChange={setPushFilter}>
                        <SelectTrigger className="w-[180px]">
                          <Filter className="mr-2 h-4 w-4" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="with_push">Com Push</SelectItem>
                          <SelectItem value="without_push">Sem Push</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" onClick={handleSelectAll}>
                        {selectedAudience !== '' ? 'Selecionar Todos do Grupo' : 'Selecionar Todos'}
                      </Button>
                      {selectedStudents.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={handleClearSelection}>
                          <X className="h-4 w-4 mr-1" />
                          Limpar ({selectedStudents.length})
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Tabela de Alunos */}
                  <div className="max-h-[350px] overflow-auto">
                    {isLoadingAudience ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="flex flex-col items-center gap-3">
                          <Clock className="h-8 w-8 text-primary animate-spin" />
                          <p className="text-sm text-muted-foreground">Carregando alunos...</p>
                        </div>
                      </div>
                    ) : (
                    <Table>
                      <TableHeader className="sticky top-0 bg-white dark:bg-gray-950 border-b">
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Aluno</TableHead>
                          <TableHead>Contato</TableHead>
                          <TableHead className="w-[120px]">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredStudents.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                              <div className="flex flex-col items-center gap-2">
                                <Search className="h-8 w-8 opacity-20" />
                                <p>Nenhum aluno encontrado</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredStudents.map((student) => (
                            <TableRow 
                              key={student.id}
                              className={`${!student.push_active ? 'opacity-50' : ''} hover:bg-gray-50 dark:hover:bg-gray-900/50`}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={selectedStudents.includes(student.id)}
                                  onCheckedChange={() => handleStudentToggle(student.id)}
                                  disabled={!student.push_active}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-10 w-10">
                                    <AvatarImage src={student.avatar_url || undefined} alt={student.name} />
                                    <AvatarFallback className="bg-primary/10 text-primary font-medium">
                                      {getInitials(student.name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="font-medium text-sm">{student.name}</p>
                                    <p className="text-xs text-muted-foreground">{student.email}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <p className="text-sm text-muted-foreground">{student.phone || '-'}</p>
                              </TableCell>
                              <TableCell>
                                {student.push_active ? (
                                  <Badge variant="default" className="bg-green-500 hover:bg-green-600">
                                    Ativo
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary">
                                    Desabilitado
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                    )}
                  </div>

                  {filteredStudents.length > 0 && (
                    <div className="p-4 border-t bg-gray-50 dark:bg-gray-900/50">
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-foreground">{selectedStudents.length}</strong> de <strong className="text-foreground">{filteredStudents.filter(s => s.push_active).length}</strong> aluno(s) selecionado(s)
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
        )}

        {/* Step 2: Mensagem */}
        {step === 2 && (
          <div className="max-w-3xl mx-auto space-y-4">
            {/* Templates Rápidos */}
            {templates.length > 0 && (
              <Card className="shadow-sm">
                <CardHeader className="border-b bg-gray-50 dark:bg-gray-900/50 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-amber-500" />
                      <div>
                        <CardTitle className="text-base">Templates Prontos</CardTitle>
                        <CardDescription className="text-xs">
                          {templates.length} template(s) disponível(is)
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  {/* Agrupar templates por categoria */}
                  {Object.entries(
                    templates.reduce((acc, template) => {
                      const category = template.category || 'general'
                      if (!acc[category]) acc[category] = []
                      acc[category].push(template)
                      return acc
                    }, {} as Record<string, typeof templates>)
                  ).map(([category, categoryTemplates]) => {
                    const categoryInfo = {
                      general: { label: 'Geral', emoji: '📢', color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900' },
                      lesson: { label: 'Aulas', emoji: '🎓', color: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-900' },
                      promotion: { label: 'Promoções', emoji: '🎁', color: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-900' },
                      alert: { label: 'Alertas', emoji: '⚠️', color: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900' },
                    }[category] || { label: category, emoji: '📝', color: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-950 dark:text-gray-300 dark:border-gray-900' }

                    return (
                      <div key={category} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{categoryInfo.emoji}</span>
                          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {categoryInfo.label}
                          </h3>
                          <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                            {categoryTemplates.length}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                          {categoryTemplates.map((template) => (
                            <button
                              key={template.id}
                              onClick={() => handleTemplateSelect(template)}
                              className={`text-left p-3 border-2 rounded-lg hover:scale-[1.02] transition-all group ${categoryInfo.color}`}
                              title={template.body}
                            >
                              <div className="flex items-start gap-2">
                                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white dark:bg-gray-900 shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                                  <span className="text-lg">{template.icon}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-xs leading-tight mb-1 line-clamp-2">
                                    {template.name}
                                  </p>
                                  <p className="text-[10px] opacity-60 line-clamp-2">
                                    {template.title}
                                  </p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                  
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-800">
                    <p className="text-xs text-muted-foreground text-center">
                      💡 Clique em um template para usar e edite conforme necessário
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Formulário de Mensagem */}
            <Card className="shadow-sm">
              <CardHeader className="border-b bg-gray-50 dark:bg-gray-900/50 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Conteúdo da Notificação</CardTitle>
                    <CardDescription className="text-xs">
                      Escreva o título e a mensagem
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Destinatários</p>
                      <p className="text-lg font-bold">{recipientCount} <span className="text-xs font-normal text-muted-foreground">receberão</span></p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setStep(1)}
                      >
                        <ArrowLeft className="mr-2 h-3 w-3" />
                        Voltar
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSubmit}
                        disabled={!title.trim() || !body.trim() || recipientCount === 0 || isSending}
                        className="px-4"
                      >
                        {isSending ? (
                          <>
                            <Clock className="mr-2 h-3.5 w-3.5 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 h-3.5 w-3.5" />
                            Enviar
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="title" className="text-xs font-medium">
                    Título da Notificação *
                  </Label>
                  <Input
                    id="title"
                    placeholder="Ex: Promoção especial! 🏄"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={100}
                    className="text-sm h-9"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {title.length}/100 caracteres
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="body" className="text-xs font-medium">
                    Mensagem *
                  </Label>
                  <Textarea
                    id="body"
                    placeholder="Ex: 50% de desconto em pacotes de 12 aulas. Válido até domingo!"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={3}
                    maxLength={200}
                    className="text-sm resize-none"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {body.length}/200 caracteres
                  </p>
                </div>

                {/* Preview */}
                {(title || body) && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-900">
                    <p className="text-[11px] font-medium text-blue-900 dark:text-blue-100 mb-1.5">Preview</p>
                    <div className="space-y-0.5">
                      {title && <p className="font-semibold text-xs">{title}</p>}
                      {body && <p className="text-xs text-muted-foreground">{body}</p>}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
